import { useMemo, useState, CSSProperties } from "react";

import { Button, Modal } from "react-bootstrap";


import { Image } from "primereact/image";


import Camera from "@/assets/svg/Camera";
import { SwitchControlledInput } from "@/common/components/SwitchControlled/SwitchControlled";
import { Status } from "@/common/enums/sharedEnums.ts";
import { useGetFile } from "@/hooks/useGetFile.tsx";
import { useCalculate } from "@/modules/dwelling/containers/DwellingEstimateContainer/Calculate/useCalculate.tsx";

import styles from "./DwellingEstimateTable.module.scss";
import { CellInput, CellSelect, CommonCell } from "../CommonCell/CommonCell";
import {
  DwellingCostResponse,
  DwellingCostToolPlansResponse,
  EquipmentPlan,
} from "../types";
import { colorsMap } from "../utils";

export type FRow = DwellingCostResponse["rooms"][number]["units"][number];

export default function useDwellingEstimateTable(
  data: DwellingCostResponse["rooms"][number],
  costingToolPlans: DwellingCostToolPlansResponse,
  name: string,
) {
  type Column = {
    header: string;
    field: string;
    body: (row: FRow) => JSX.Element;
    style?: CSSProperties;
  };

  const isEmpty = data?.units?.length === 0 || data === undefined;

  const [showModal, setShowModal] = useState<{
    open: boolean;
    row: FRow | null;
  }>({
    open: false,
    row: null,
  });

  const {
    costingCalculate,
    handleChangeQuantity,
    handleChangePreTaxPrice,
    handleChangeTaxPercentage,
    handleChangeDilapidationPercentage,
    handleChangeSelect,
    handleChangeSelectRow,
    handleChangeUnit,
  } = useCalculate();

  const repairOptions = useMemo(
    () =>
      costingToolPlans.map((plan) => ({
        label: plan.label,
        options: plan.equipmentPlans.map((option) => ({
          value: option.id,
          label: `${option.description} - ${option.label}`,
          data: option.label,
        })),
      })),
    [costingToolPlans],
  );

  const getPlanByOptionSelected = (option: {
    value: string;
    label: string;
  }): EquipmentPlan => {
    const plan = costingToolPlans.find((plan) =>
      plan.equipmentPlans.some(
        (equipmentPlan) => equipmentPlan.id === option.value,
      ),
    );

    if (!plan) {
      throw new Error("Plan not found");
    }

    return plan.equipmentPlans.find(
      (equipmentPlan) => equipmentPlan.id === option.value,
    ) as EquipmentPlan;
  };

  const ModalPhoto = () => {
    const incomingPath = showModal.row?.incoming?.pictures ?? [];
    const outgoingPath = showModal.row?.outgoing?.pictures ?? [];

    const { data: incomingPictures } = useGetFile({
      enabled: incomingPath.length > 0,
      listFiles: incomingPath || [],
      keys: [
        "costing",
        "unit",
        "incoming",
        "pictures",
        // @ts-expect-error the row cannot be null at this step
        showModal.row?.incoming?.id,
      ],
    });

    const { data: outgoingPictures } = useGetFile({
      enabled: outgoingPath.length > 0,
      listFiles: outgoingPath || [],
      keys: [
        "costing",
        "unit",
        "outgoing",
        "pictures",
        // @ts-expect-error the row cannot be null at this step
        showModal.row?.outgoing?.id,
      ],
    });

    return (
      <Modal
        centered={true}
        size={"xl"}
        show={showModal.open}
        onHide={() =>
          setShowModal({
            open: false,
            row: null,
          })
        }
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Photos : {showModal.row?.name}
          </Modal.Title>
        </Modal.Header>
        <div className={"p-4"}>
          {incomingPictures?.urls && incomingPictures?.urls?.length > 0 ? (
            <>
              <h3>Entrée</h3>
              <div className="row">
                <div className={"position-relative"}>
                  {incomingPictures.urls.map((picture, index) => (
                    <Image
                      className={"m-2 rounded overflow-hidden"}
                      key={index}
                      width="250"
                      src={picture}
                      alt={picture}
                      preview
                    />
                  ))}
                </div>
              </div>
            </>
          ) : undefined}
          {outgoingPictures?.urls && outgoingPictures?.urls?.length > 0 ? (
            <>
              <h3>Sortie</h3>
              <div className="row">
                <div className={"position-relative"}>
                  {outgoingPictures.urls.map((picture, index) => (
                    <Image
                      className={"m-2 rounded overflow-hidden"}
                      key={index}
                      width="250"
                      src={picture}
                      alt={picture}
                      preview
                    />
                  ))}
                </div>
              </div>
            </>
          ) : undefined}
        </div>
      </Modal>
    );
  };

  const entranceTable = [
    {
      header: "",
      field: "",
      body: (row: FRow) => (
        <SwitchControlledInput
          checked={
            costingCalculate.elements[name].selected[data.units.indexOf(row)]
          }
          onChangeCallback={() =>
            handleChangeSelectRow(name, data.units.indexOf(row))
          }
          name={`${data.id}_${data.units.indexOf(row)}_${row.name}_isSelected`}
        />
      ),
    },
    {
      header: "Quantité",
      field: "incomingQuantity",
      body: (row: FRow) => (
        <CellInput
          type="number"
          style={{ width: 40 }}
          defaultValue={row?.incoming?.quantity.toString()}
        />
      ),
    },
    {
      header: "Unité de constat",
      field: "name",
      body: (row: FRow) => <CellInput defaultValue={row?.name} />,
    },
    {
      header: "Observations",
      style: { paddingInlineEnd: 0 },
      field: "incomingObservations",
      body: (row: FRow) => (
        <CellInput
          defaultValue={row?.incoming?.observation || "-"}
          name={`${data.id}_${data.units.indexOf(row)}_${
            row.name
          }_incomingObservations`}
        />
      ),
    },
    {
      header: "État",
      field: "incomingStatus",
      body: (row: FRow) => (
        <CommonCell
          title={Status[row?.incoming?.status as keyof typeof Status]}
        >
          <div
            className={styles.statusColor}
            style={{
              backgroundColor:
                colorsMap[row?.incoming?.status as keyof typeof colorsMap]
                  ?.color,
            }}
          />
        </CommonCell>
      ),
    },
    {
      header: "État de marche",
      field: "incomingOperatingStatus",
      body: (row: FRow) => (
        <CellInput
          disabled={true}
          style={{ width: 40 }}
          defaultValue={row?.incoming?.operatingStatus ?? ""}
        />
      ),
    },
  ] satisfies Column[];

  const exitTable = [
    {
      header: "Quantité",
      field: "outgoingQuantity",
      body: (row: FRow) => (
        <CellInput
          type="number"
          style={{ width: 40 }}
          defaultValue={row?.outgoing?.quantity.toString()}
        />
      ),
    },
    {
      header: "Observations",
      style: { paddingInlineEnd: 0 },
      field: "outgoingObservations",
      body: (row: FRow) => (
        <CellInput
          defaultValue={row.outgoing.observation || "-"}
          name={`${data.id}_${data.units.indexOf(row)}_${
            row.name
          }_outgoingObservations`}
          style={{ borderRadius: "8px 0 0 8px" }}
        />
      ),
    },
    {
      header: "",
      style: { paddingInlineStart: 0 },
      field: "",
      body: (row) => {
        const isActive =
          row?.incoming?.pictures?.length > 0 ||
          row?.outgoing?.pictures?.length > 0;
        return (
          <Button
            onClick={() =>
              setShowModal({
                open: true,
                row,
              })
            }
            disabled={!isActive}
            type="button"
            variant="primary"
            className={styles.cameraIconContainer}
          >
            <Camera color="white" />
          </Button>
        );
      },
    },
    {
      header: "État",
      field: "outgoingStatus",
      body: (row: FRow) => (
        <CommonCell
          title={Status[row?.outgoing?.status as keyof typeof Status]}
        >
          <div
            className={styles.statusColor}
            style={{
              backgroundColor:
                colorsMap[row?.outgoing?.status as keyof typeof colorsMap]
                  ?.color,
            }}
          />
        </CommonCell>
      ),
    },
    {
      header: "État de marche",
      field: "outgoingOperatingStatus",
      body: (row: FRow) => (
        <CellInput
          disabled={true}
          style={{ width: 70 }}
          defaultValue={row.outgoing.operatingStatus ?? ""}
        />
      ),
    },
  ] satisfies Column[];

  const costingTable = [
    {
      header: "Réparation associée",
      field: "planId",
      body: (row: FRow) => {
        const value = costingCalculate.elements[name].rows[
          data.units.indexOf(row)
        ]?.plan?.description
          ? {
              value:
                costingCalculate.elements[name].rows[data.units.indexOf(row)]
                  ?.plan?.id,
              label:
                costingCalculate.elements[name].rows[data.units.indexOf(row)]
                  ?.plan?.description,
              hover: `${
                costingCalculate.elements[name].rows[data.units.indexOf(row)]
                  ?.plan?.description
              } -  ${
                costingCalculate.elements[name].rows[data.units.indexOf(row)]
                  ?.plan?.label
              }
              `,
            }
          : undefined;

        return (
          <CellSelect
            tooltipText={value?.hover || ""}
            onChange={(optSelected: { label: string; value: string }) => {
              const plan = getPlanByOptionSelected(optSelected);
              handleChangeSelect(name, data.units.indexOf(row), plan);
            }}
            placeholder="Réparation"
            value={value}
            defaultValue={"Réparation"}
            options={repairOptions}
          />
        );
      },
    },
    {
      header: "Quantité",
      field: "cost-quantity",
      body: (row: FRow) => (
        <CellInput
          min={0}
          type="number"
          onChange={(e) =>
            handleChangeQuantity(name, data.units.indexOf(row), e.target.value)
          }
          value={
            costingCalculate.elements[name].rows[data.units.indexOf(row)]
              ?.quantity
          }
        />
      ),
    },
    {
      header: "Unité",
      field: "unit",
      body: (row: FRow) => {
        return (
          <CellInput
            type="text"
            onChange={(e) =>
              handleChangeUnit(name, data.units.indexOf(row), e.target.value)
            }
            value={
              costingCalculate.elements[name].rows[data.units.indexOf(row)]
                ?.plan?.unit
            }
          />
        );
      },
    },
    {
      header: "PU HT",
      field: "priceTaxExcluded",
      body: (row: FRow) => (
        <CellInput
          type="number"
          min={0}
          extension="€"
          onChange={(e) =>
            handleChangePreTaxPrice(
              name,
              data.units.indexOf(row),
              e.target.value,
            )
          }
          value={
            costingCalculate.elements[name].rows[data.units.indexOf(row)]
              ?.preTaxPrice
          }
        />
      ),
    },
    {
      header: "TVA",
      field: "taxPercentage",
      body: (row: FRow) => (
        <CellInput
          min={0}
          max={100}
          type="number"
          extension="%"
          onChange={(e) =>
            handleChangeTaxPercentage(
              name,
              data.units.indexOf(row),
              e.target.value,
            )
          }
          value={
            costingCalculate.elements[name].rows[data.units.indexOf(row)]
              ?.taxPercentage
          }
        />
      ),
    },
    {
      header: "Vétusté",
      field: "dilapidation.rate",
      body: (row: FRow) => (
        <CellInput
          min={0}
          max={100}
          type="number"
          extension="%"
          onChange={(e) =>
            handleChangeDilapidationPercentage(
              name,
              data.units.indexOf(row),
              e.target.value,
            )
          }
          value={
            costingCalculate.elements[name].rows[data.units.indexOf(row)]
              ?.dilapidation?.rate
          }
        />
      ),
    },
    {
      header: "Montant TTC",
      field: "priceTaxIncluded",
      body: (row: FRow) => (
        <CommonCell>
          {costingCalculate.elements[name].rows[data.units.indexOf(row)]
            ?.taxIncludedPrice + "€"}
        </CommonCell>
      ),
    },
  ] satisfies Column[];

  return {
    entranceTable,
    exitTable,
    costingTable,
    ModalPhoto,
    isEmpty,
  };
}
